/**
 * Cliente HTTP da API — docs/02-ARQUITETURA.md §5.
 *
 * Autenticacao por COOKIE de sessao (Sanctum SPA), nao por token em
 * localStorage: token acessivel por JavaScript e token roubavel por XSS.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    /** Codigo legivel por maquina: trate por ele, nao pelo texto da mensagem. */
    public readonly code: string,
    message: string,
    public readonly errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Primeira mensagem de erro de um campo especifico. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0];
  }
}

/**
 * Le o cookie XSRF-TOKEN na hora de cada requisicao.
 *
 * NAO cachear: o login chama session()->regenerate() para prevenir fixacao de
 * sessao, e isso ROTACIONA o token. Um valor guardado antes do login faz a
 * primeira chamada seguinte receber 419.
 */
function xsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

let csrfInitialized = false;

/** Obtem o cookie CSRF. Idempotente: so vale a pena na primeira vez. */
async function ensureCsrfCookie(): Promise<void> {
  if (csrfInitialized) return;
  await fetch('/sanctum/csrf-cookie', { credentials: 'same-origin' });
  csrfInitialized = true;
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** IDs de entidade do seletor de contexto. O backend faz INTERSECAO com o permitido. */
  entityIds?: number[];
  signal?: AbortSignal;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, entityIds, signal } = options;

  if (method !== 'GET') {
    await ensureCsrfCookie();
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const token = xsrfToken();
  if (token) headers['X-XSRF-TOKEN'] = token;

  if (entityIds?.length) headers['X-Entity-Ids'] = entityIds.join(',');

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    // 419 = token CSRF expirado. Renova e deixa o chamador tentar de novo,
    // em vez de repetir automaticamente: repetir POST as cegas pode duplicar
    // um lancamento.
    if (response.status === 419) {
      csrfInitialized = false;
      await ensureCsrfCookie();
    }

    throw new ApiError(
      response.status,
      payload?.code ?? 'UNKNOWN',
      payload?.message ?? `Erro ${response.status}`,
      payload?.errors,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  /**
   * DELETE aceita corpo — dentro das OPCOES, nao como segundo argumento.
   *
   * Operacoes destrutivas deste sistema exigem justificativa: excluir
   * lancamento liquidado (RN-131), remover backup, cancelar acerto. O motivo
   * vai no corpo e termina na auditoria; na query string ele acabaria no log
   * de acesso do servidor, que nao e lugar para texto do usuario.
   *
   * O corpo NAO virou segundo parametro de proposito. Todas as chamadas
   * existentes passam `{ entityIds }` nessa posicao — promove-lo a corpo
   * mandaria o seletor de entidade dentro do JSON e faria o header
   * `X-Entity-Ids` desaparecer, sem erro de tipo e sem erro em tempo de
   * execucao. Falha silenciosa em filtro de escopo e o pior tipo.
   */
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  upload: <T>(path: string, file: File, onProgress?: (percent: number) => void) =>
    upload<T>(path, file, onProgress),

  download: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    download(path, options),
};

/**
 * Baixa um arquivo gerado pela API — RF-REL-11.
 *
 * ================================================================
 * POR QUE NAO E SO UM <a href="/api/v1/...">
 * ================================================================
 *
 * Um link comum navega, e navegando o navegador NAO manda o header
 * `X-Entity-Ids`. O arquivo sairia com o recorte errado — todas as entidades em
 * vez das selecionadas — e nada na tela indicaria isso. O usuario receberia um
 * PDF que parece certo e contem dados de outra entidade.
 *
 * Buscando por `fetch` os headers vao iguais aos da tela, e o arquivo sai do
 * mesmo recorte que esta no monitor.
 *
 * O nome vem do `Content-Disposition` que o servidor mandou. Inventar o nome
 * aqui duplicaria uma regra que ja existe no backend, e os dois divergiriam.
 */
async function download(
  path: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<void> {
  const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };

  if (options.entityIds?.length) headers['X-Entity-Ids'] = options.entityIds.join(',');

  const response = await fetch(`/api/v1${path}`, {
    headers,
    credentials: 'same-origin',
    signal: options.signal,
  });

  if (!response.ok) {
    // O corpo de um erro continua sendo JSON, mesmo num endpoint de arquivo:
    // e assim que a recusa por excesso de linhas chega com o motivo legivel.
    const payload = await response.json().catch(() => null);

    throw new ApiError(
      response.status,
      payload?.code ?? 'UNKNOWN',
      payload?.message ?? `Erro ${response.status}`,
      payload?.errors,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileNameFromHeader(response.headers.get('Content-Disposition')) ?? 'relatorio';
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Sem revoke, cada exportacao deixa o arquivo inteiro preso na memoria da aba
  // ate a pagina recarregar.
  URL.revokeObjectURL(url);
}

/** `attachment; filename="dre-gerencial-2026-08-09.pdf"` -> o nome. */
export function fileNameFromHeader(header: string | null): string | null {
  if (!header) return null;

  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match?.[1]) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Envio de arquivo — usa XMLHttpRequest, e nao `fetch`.
 *
 * `fetch` nao reporta progresso de UPLOAD (so de download). Um backup pode ter
 * dezenas de megabytes numa conexao domestica: sem barra, a tela fica parada
 * por minutos e o usuario conclui que travou e recarrega a pagina no meio do
 * envio. XHR e a unica API do navegador que expoe `upload.onprogress`.
 *
 * NAO define Content-Type: com FormData quem precisa escrever o cabecalho e o
 * navegador, porque so ele conhece o `boundary` do multipart. Defini-lo a mao
 * produz um corpo que o servidor nao consegue separar em campos.
 */
async function upload<T>(
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<T> {
  await ensureCsrfCookie();

  const form = new FormData();
  form.append('file', file);

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/v1${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    const token = xsrfToken();
    if (token) xhr.setRequestHeader('X-XSRF-TOKEN', token);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      const payload = (() => {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return null;
        }
      })();

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload as T);
        return;
      }

      if (xhr.status === 419) csrfInitialized = false;

      // 413 vem do nginx, ANTES do Laravel: a resposta e HTML, sem `message`.
      // Sem este caso o usuario receberia "Erro 413" e nenhuma pista do limite.
      reject(
        new ApiError(
          xhr.status,
          payload?.code ?? 'UNKNOWN',
          payload?.message ??
            (xhr.status === 413
              ? 'O arquivo e grande demais para ser enviado pelo navegador.'
              : `Erro ${xhr.status}`),
          payload?.errors,
        ),
      );
    };

    xhr.onerror = () =>
      reject(new ApiError(0, 'NETWORK', 'A conexao caiu durante o envio. Tente novamente.'));

    xhr.onabort = () => reject(new ApiError(0, 'ABORTED', 'Envio cancelado.'));

    xhr.send(form);
  });
}
